// src/views/knowledge-base/KnowledgeBasePanel.tsx
import { useEffect, useMemo, useState } from 'react'
import type { KbTree, KbTutorialFull, KbCategory, KbSubcategory, KbTutorialMeta } from './types'
import { kbApi } from './api'
import { startWalkthrough } from './WalkthroughPlayer'
import { displaySteps } from './steps-utils'
import './KnowledgeBasePanel.css'

type Props = { open: boolean; onClose: () => void; isSuperAdmin: boolean }

export function KnowledgeBasePanel({ open, onClose, isSuperAdmin }: Props) {
  const [tree, setTree] = useState<KbTree>([])
  const [query, setQuery] = useState('')
  const [openCats, setOpenCats] = useState<Set<number>>(new Set())
  const [openSubs, setOpenSubs] = useState<Set<number>>(new Set())
  const [activeTutorialId, setActiveTutorialId] = useState<number | null>(null)
  const [activeTutorial, setActiveTutorial] = useState<KbTutorialFull | null>(null)

  useEffect(() => {
    if (!open) return
    kbApi.tree().then(setTree).catch(console.error)
  }, [open])

  useEffect(() => {
    if (activeTutorialId == null) { setActiveTutorial(null); return }
    kbApi.tutorial(activeTutorialId).then(setActiveTutorial).catch(console.error)
  }, [activeTutorialId])

  const filtered = useMemo<KbTree>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tree
    return tree
      .map<KbCategory | null>(cat => {
        const catMatch = cat.name.toLowerCase().includes(q)
        const subs = cat.subcategories
          .map<KbSubcategory | null>(sub => {
            const subMatch = sub.name.toLowerCase().includes(q)
            const tuts = sub.tutorials.filter(t => t.title.toLowerCase().includes(q) || subMatch || catMatch)
            if (tuts.length === 0 && !subMatch && !catMatch) return null
            return { ...sub, tutorials: subMatch || catMatch ? sub.tutorials : tuts }
          })
          .filter((s): s is KbSubcategory => s !== null)
        if (subs.length === 0 && !catMatch) return null
        return { ...cat, subcategories: catMatch ? cat.subcategories : subs }
      })
      .filter((c): c is KbCategory => c !== null)
  }, [tree, query])

  if (!open) return null

  const toggle = (set: Set<number>, setSet: (s: Set<number>) => void, id: number) => {
    const next = new Set(set)
    next.has(id) ? next.delete(id) : next.add(id)
    setSet(next)
  }

  return (
    <aside className={`kb-panel ${activeTutorialId != null ? 'kb-panel--wide' : ''}`}>
      <header className="kb-panel__header">
        {activeTutorialId != null ? (
          <button className="kb-panel__back" onClick={() => { setActiveTutorialId(null) }}>← Back</button>
        ) : <h2>Help Center</h2>}
        <div className="kb-panel__header-actions">
          {isSuperAdmin && activeTutorialId == null && (
            <button title="Manage" className="kb-panel__icon" onClick={() => { onClose(); window.location.hash = '#/admin/knowledge-base' }}>⚙</button>
          )}
          <button className="kb-panel__icon" onClick={onClose}>✕</button>
        </div>
      </header>

      {activeTutorialId == null ? (
        <>
          <input className="kb-panel__search" placeholder="Search tutorials…" value={query} onChange={e => setQuery(e.target.value)} />
          <div className="kb-panel__tree">
            {filtered.length === 0 && <div className="kb-panel__empty">No tutorials yet.</div>}
            {filtered.map(cat => (
              <div key={cat.id} className="kb-cat">
                <button className="kb-cat__head" onClick={() => toggle(openCats, setOpenCats, cat.id)}>
                  <span className={`kb-chev ${openCats.has(cat.id) ? 'kb-chev--open' : ''}`}>›</span> {cat.name}
                </button>
                {openCats.has(cat.id) && cat.subcategories.map(sub => (
                  <div key={sub.id} className="kb-sub">
                    <button className="kb-sub__head" onClick={() => toggle(openSubs, setOpenSubs, sub.id)}>
                      <span className={`kb-chev ${openSubs.has(sub.id) ? 'kb-chev--open' : ''}`}>›</span> {sub.name}
                    </button>
                    {openSubs.has(sub.id) && sub.tutorials.map((t: KbTutorialMeta) => (
                      <button key={t.id} className="kb-tut" onClick={() => setActiveTutorialId(t.id)}>
                        {t.title}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="kb-detail">
          {!activeTutorial ? <div className="kb-panel__empty">Loading…</div> : (
            <>
              <h3>{activeTutorial.tutorial.title}</h3>
              <div className="kb-detail__actions">
                <button className="kb-btn kb-btn--primary" onClick={() => {
                  onClose()
                  startWalkthrough({
                    steps: activeTutorial.steps,
                    navigate: (to: string) => { window.location.hash = to.startsWith('#') ? to : `#${to.startsWith('/') ? to : '/' + to}` },
                    onExit: () => {},
                    onDone: () => {},
                    tutorialTitle: activeTutorial.tutorial.title,
                    tutorialId: activeTutorial.tutorial.id,
                  })
                }}>▶ Run Walkthrough</button>
              </div>
              <ol className="kb-steps">
                {displaySteps(activeTutorial.steps).map((s, idx) => (
                  <li key={s.id}><strong>{idx + 1}.</strong> {s.text}</li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </aside>
  )
}
